import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";
import { env } from "../utils/env";
import { Permission } from "../types/Permission";

const fetchAccessPermissions = (authHeader: string, roleId?: number) => () =>
  axios.get<Permission[]>(`${env.verifyServiceUrl}user/permission/`, {
    headers: {
      Authorization: authHeader,
    },
    params: typeof roleId === "number" ? { role: roleId } : undefined,
  });

/**
 * Tenant-wide permission catalogue, backing the Permissions tab on a Role's
 * preview slider. Named distinctly from the legacy `usePermissionGroups`
 * (`user/groups/`) — that's the old permission-group system this SEG Role
 * model is replacing, not the same endpoint.
 *
 * Pass `roleId` to scope each permission's `is_granted` to that Role (see
 * the caveat on `Permission.is_granted` — the `?role=` filter param isn't
 * confirmed against the real schema, only inferred from every sample row
 * otherwise coming back `false`). Omit it for a brand new Role, where
 * "everything ungranted" is correct anyway.
 */
export const useAccessPermissions = (roleId?: number) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  return useQuery(
    ["accessPermissions", roleId ?? null],
    fetchAccessPermissions(authHeader, roleId),
    {
      retry: false,
      onError: (err: any) => {
        if (err.request?.status === 500 || err.request?.status === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }
        if (err.request?.status === 401) {
          navigate(ROUTE.LOGOUT);
        }
      },
    }
  );
};
